import { useTheme } from '@mui/material'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ZodObject } from 'zod'
import { IFormSchemaVersion } from '../../generated-types/form-schema'
import { useZodForm } from '../../hooks/zod-form'
import Form from '../../screens/customer/form/Form'
import { AppForm, FormSubmitButton } from '../FormCore'
import { GenericDialogLayout } from '../GenericDialogLayout'
import { MainButton } from '../MainButton'

interface IFormSchemaForm {
  version: IFormSchemaVersion
  title: string
  schema: ZodObject<any>
  pop: () => void
  onSubmit: (values: any) => void
  initialValues: object
  onSaveDraft?: (values: any) => void
  isDraft?: boolean
  submitText?: string
  draftButtonText?: string
}

const FormSchemaForm: React.FC<IFormSchemaForm> = ({
  version,
  title,
  schema,
  pop,
  onSubmit,
  initialValues,
  onSaveDraft,
  isDraft,
  submitText,
  draftButtonText,
}) => {
  const { t } = useTranslation(['common', 'form-schema-report'])
  const theme = useTheme()

  const form = useZodForm(schema, { defaultValues: initialValues })
  const onSubmitHandler = async () => {
    onSubmit(form.getValues())
  }

  useEffect(() => {
    form.reset(initialValues as Parameters<typeof form.reset>[0])
  }, [schema, initialValues, form])

  const dialogActions = [
    ...(onSaveDraft
      ? [
          <MainButton key="save-draft" type="button" variant="outlined" onClick={() => onSaveDraft(form.getValues())}>
            {draftButtonText ?? t('save-as-draft', { ns: 'form-schema-report' })}
          </MainButton>,
        ]
      : []),
    <FormSubmitButton key="submit" createText={submitText ?? t('save')} />,
  ]

  return (
    <AppForm form={form} onSubmit={onSubmitHandler} hasInitialValues={false}>
      <GenericDialogLayout
        title={title}
        titleTypography={{ variant: 'h6', fontWeight: 600, sx: { color: 'text.primary' } }}
        titleSectionTopPadding="16px"
        titleSectionBottomPadding="8px"
        titleSectionXPadding="20px"
        titleDivider
        actionsDivider
        closeIconSize="1.25rem"
        removedCloseButton={false}
        pop={pop}
        sx={{
          px: 2,
          pt: 1.5,
          pb: 1,
          maxHeight: `min(90vh, ${theme.spacing(120)})`,
          overflow: 'auto',
          flex: 1,
          minHeight: 0,
        }}
        actions={dialogActions}
      >
        <Form
          layouts={JSON.parse(version.layout)}
          form={form}
          density="compact"
          disabled={undefined}
          onDrop={undefined}
          addLayout={undefined}
          deleteLayout={undefined}
          selectFormField={undefined}
          title={title}
          onRearrange={undefined}
          isDraft={isDraft}
        />
      </GenericDialogLayout>
    </AppForm>
  )
}

export default FormSchemaForm
